import { useMemo } from 'react';
import { X, GitCompareArrows } from 'lucide-react';

interface ProposalDiffDialogProps {
  isOpen: boolean;
  title: string;
  currentLabel: string;
  proposedLabel: string;
  currentContent: string;
  proposedContent: string;
  onClose: () => void;
}

interface DiffLine {
  leftNumber: number | null;
  rightNumber: number | null;
  leftText: string;
  rightText: string;
  changed: boolean;
}

function buildDiffLines(currentContent: string, proposedContent: string): DiffLine[] {
  const before = currentContent.split('\n');
  const after = proposedContent.split('\n');
  const length = Math.max(before.length, after.length);
  const lines: DiffLine[] = [];

  for (let index = 0; index < length; index++) {
    const leftText = before[index] ?? '';
    const rightText = after[index] ?? '';
    const hasLeft = index < before.length;
    const hasRight = index < after.length;

    lines.push({
      leftNumber: hasLeft ? index + 1 : null,
      rightNumber: hasRight ? index + 1 : null,
      leftText,
      rightText,
      changed: leftText !== rightText,
    });
  }

  return lines;
}

export default function ProposalDiffDialog({
  isOpen,
  title,
  currentLabel,
  proposedLabel,
  currentContent,
  proposedContent,
  onClose,
}: ProposalDiffDialogProps) {
  const diffLines = useMemo(
    () => buildDiffLines(currentContent, proposedContent),
    [currentContent, proposedContent],
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={onClose}>
      <div
        className="flex h-[78vh] w-[92vw] max-w-6xl flex-col overflow-hidden rounded-lg border border-[#444] bg-[#1e1e1e] shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[#333] px-5 py-3">
          <div className="flex items-center gap-2">
            <GitCompareArrows className="h-4 w-4 text-purple-300" />
            <div>
              <div className="text-sm font-semibold text-white">{title}</div>
              <div className="text-[11px] text-[#8b949e]">Review current file against the proposed AI change</div>
            </div>
          </div>
          <button onClick={onClose} className="text-[#858585] transition-colors hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-2 divide-x divide-[#333] overflow-hidden">
          <div className="min-h-0 overflow-auto">
            <div className="sticky top-0 z-10 border-b border-[#333] bg-[#252526] px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-[#9ca3af]">
              {currentLabel}
            </div>
            <div className="font-mono text-[11px] leading-5">
              {diffLines.map((line, index) => (
                <div
                  key={`left-${index}`}
                  className={line.changed ? 'bg-red-500/10' : 'bg-transparent'}
                >
                  <div className="grid grid-cols-[56px_1fr] gap-3 px-4 py-0.5">
                    <span className="select-none text-right text-[#6e7681]">{line.leftNumber ?? ''}</span>
                    <pre className="whitespace-pre-wrap break-words text-[#d4d4d4]">{line.leftText || ' '}</pre>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="min-h-0 overflow-auto">
            <div className="sticky top-0 z-10 border-b border-[#333] bg-[#252526] px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-[#9ca3af]">
              {proposedLabel}
            </div>
            <div className="font-mono text-[11px] leading-5">
              {diffLines.map((line, index) => (
                <div
                  key={`right-${index}`}
                  className={line.changed ? 'bg-green-500/10' : 'bg-transparent'}
                >
                  <div className="grid grid-cols-[56px_1fr] gap-3 px-4 py-0.5">
                    <span className="select-none text-right text-[#6e7681]">{line.rightNumber ?? ''}</span>
                    <pre className="whitespace-pre-wrap break-words text-[#d4d4d4]">{line.rightText || ' '}</pre>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}